import {
  comparePromptModelTiers,
  getPromptModelSuggestion,
  isPromptModelSupported,
  resolvePromptModelSuggestion,
  type PromptLlmModel,
  type PromptLlmModelProvider,
  type PromptLlmModelTier,
  type PromptTaskType,
} from "@nexus/contracts";

/** Select value for null model (service default at call sites). */
export const PROMPT_MODEL_SERVICE_DEFAULT_VALUE = "__service_default__";

export type PromptModelTierWarning = "over" | "under";

/**
 * Soft tier mismatch vs recommended for the current usedIn.
 * - over: selected tier above recommended (cost)
 * - under: selected below recommended when recommended is premium (quality)
 */
export function getPromptModelTierWarning(
  selectedTier: PromptLlmModelTier | null | undefined,
  usedIn: string,
): PromptModelTierWarning | null {
  if (!selectedTier || !usedIn) return null;
  const suggestion = getPromptModelSuggestion(usedIn);
  if (!suggestion) return null;

  const cmp = comparePromptModelTiers(selectedTier, suggestion.recommendedTier);
  if (cmp > 0) return "over";
  if (cmp < 0 && suggestion.recommendedTier === "premium") return "under";
  return null;
}

/**
 * The catalog suggestion, but only when the call site can run it. A call site
 * that ignores the prompt's model (or runs the other provider) gets null, so a
 * new prompt saves with the service default instead of a model the API rejects.
 */
export function resolveRunnablePromptModelSuggestion(
  usedIn: string,
  catalog: readonly PromptLlmModel[],
  taskType: PromptTaskType,
): PromptLlmModel | null {
  const suggestion = resolvePromptModelSuggestion(usedIn, catalog, taskType);
  if (!suggestion || !isPromptModelSupported(usedIn, suggestion.id)) return null;
  return suggestion;
}

export function modelIdFromSelectValue(value: string): string | null {
  return value === PROMPT_MODEL_SERVICE_DEFAULT_VALUE ? null : value;
}

export function selectValueFromModelId(modelId: string | null): string {
  return modelId ?? PROMPT_MODEL_SERVICE_DEFAULT_VALUE;
}

export function modelSupportsPromptTask(
  model: PromptLlmModel,
  taskType: PromptTaskType,
): boolean {
  return model.capabilities.includes(taskType);
}

/**
 * Why a model cannot run this prompt: Vertex will not let this project invoke
 * it at all ("unavailable"), the worker behind the call site talks to one
 * provider ("provider"), or the model cannot produce what the prompt asks for
 * ("capability"). Null means it runs.
 */
export type PromptModelBlockedReason = "unavailable" | "provider" | "capability";

export function promptModelBlockedReason(
  model: PromptLlmModel,
  usedIn: string,
  taskType: PromptTaskType,
): PromptModelBlockedReason | null {
  if (model.unavailable) return "unavailable";
  if (!isPromptModelSupported(usedIn, model.id)) return "provider";
  if (!modelSupportsPromptTask(model, taskType)) return "capability";
  return null;
}

/**
 * Locked models stay in the default list. Hiding them is what left "why can't I
 * pick Opus 5?" answerable only by reading a worker log; a greyed row with a
 * reason turns that into something an admin can act on. Models blocked by
 * provider or capability stay behind "Show all", where they are noise.
 */
export function isVisibleByDefault(
  reason: PromptModelBlockedReason | null,
): boolean {
  return reason === null || reason === "unavailable";
}

/** i18n key for disabled-row reason when "Show all models" is on. */
export function incapabilityReasonKey(taskType: PromptTaskType): string {
  if (taskType === "image") return "prompts.editor.model.incapableImage";
  if (taskType === "vision") return "prompts.editor.model.incapableVision";
  return "prompts.editor.model.incapableText";
}

/** Best-effort provider for an id the catalog no longer carries. */
export function inferPromptModelProvider(
  modelId: string | null | undefined,
): PromptLlmModelProvider | "service" {
  if (!modelId) return "service";
  if (modelId.startsWith("claude-")) return "anthropic";
  if (modelId.startsWith("gemini-")) return "google";
  return "service";
}

const PROVIDER_ORDER: PromptLlmModelProvider[] = ["anthropic", "google"];

/** Within a provider, keep related SKUs together and ordered premium → economy. */
const FAMILY_CLUSTER_ORDER = [
  "opus",
  "sonnet",
  "haiku",
  "pro",
  "flash",
  "image",
  "other",
] as const;
export type PromptModelFamilyCluster = (typeof FAMILY_CLUSTER_ORDER)[number];

export function promptModelFamilyCluster(modelId: string): PromptModelFamilyCluster {
  const id = modelId.toLowerCase();
  if (id.includes("image")) return "image";
  if (id.includes("opus")) return "opus";
  if (id.includes("sonnet")) return "sonnet";
  if (id.includes("haiku")) return "haiku";
  if (id.includes("flash")) return "flash";
  if (id.includes("pro")) return "pro";
  return "other";
}

/** Group catalog models by provider; optionally omit an id already shown as Suggested. */
export function groupPromptModelsByProvider(
  models: readonly PromptLlmModel[],
  omitModelId?: string | null,
): { provider: PromptLlmModelProvider; models: PromptLlmModel[] }[] {
  const byProvider = new Map<PromptLlmModelProvider, PromptLlmModel[]>();
  for (const model of models) {
    if (omitModelId && model.id === omitModelId) continue;
    const list = byProvider.get(model.provider) ?? [];
    list.push(model);
    byProvider.set(model.provider, list);
  }

  return PROVIDER_ORDER.filter((p) => (byProvider.get(p)?.length ?? 0) > 0).map((provider) => ({
    provider,
    models: byProvider.get(provider) ?? [],
  }));
}

/** Split a provider's models into family clusters (Opus / Sonnet / …) for clearer lists. */
export function clusterPromptModelsByFamily(
  models: readonly PromptLlmModel[],
): { cluster: PromptModelFamilyCluster; models: PromptLlmModel[] }[] {
  const byCluster = new Map<PromptModelFamilyCluster, PromptLlmModel[]>();
  for (const model of models) {
    const cluster = promptModelFamilyCluster(model.id);
    const list = byCluster.get(cluster) ?? [];
    list.push(model);
    byCluster.set(cluster, list);
  }

  return FAMILY_CLUSTER_ORDER.filter((c) => (byCluster.get(c)?.length ?? 0) > 0).map(
    (cluster) => ({
      cluster,
      models: lockedLast(byCluster.get(cluster) ?? []),
    }),
  );
}

/**
 * Most of the Claude catalog is locked for this project, so a plain id sort
 * buries the two models anyone can pick. Locked rows stay visible, just never
 * above a usable one.
 */
function lockedLast(models: PromptLlmModel[]): PromptLlmModel[] {
  return [...models].sort(
    (a, b) => Number(a.unavailable ?? false) - Number(b.unavailable ?? false),
  );
}
